import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiClock, FiAward, FiUsers, FiArrowRight } from 'react-icons/fi';

const CardContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-6px) rotateX(2deg);
    box-shadow: 
      0 20px 25px -5px rgba(0, 0, 0, 0.15),
      0 10px 10px -5px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.colors.primary}40;

    &::before {
      transform: scaleX(1);
    }
  }

  &:active {
    transform: translateY(-3px);
  }
`;

const CourseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const CourseCode = styled.div`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 8px ${props => props.theme.colors.primary}40;
`;

const LevelBadge = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.primary}10);
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid ${props => props.theme.colors.primary}30;
`;

const CourseTitle = styled.h3`
  color: ${props => props.theme.colors.heading};
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.4;
  transition: color 0.3s ease;

  ${CardContainer}:hover & {
    color: ${props => props.theme.colors.primary};
  }
`;

const CourseDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
`;

const CourseDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;

  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1rem;
  }
`;

const JobOutcomes = styled.div`
  background: ${props => props.theme.colors.divider};
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid ${props => props.theme.colors.primary};
`;

const JobOutcomesTitle = styled.h4`
  color: ${props => props.theme.colors.heading};
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const JobOutcomesList = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
`;

const EntryRequirements = styled.div`
  background: ${props => props.theme.colors.divider};
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid ${props => props.theme.colors.primary}60;
`;

const EntryRequirementsTitle = styled.h4`
  color: ${props => props.theme.colors.heading};
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const EntryRequirementsText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
`;

const ViewButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
  padding: 0.875rem 1.5rem;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 4px 12px ${props => props.theme.colors.primary}40;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px ${props => props.theme.colors.primary}60;
    color: white;

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 1rem;
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #ffd200, #ffb300);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(255, 210, 0, 0.4);
  transform: rotate(15deg);
`;

const CourseCard = ({ course, categoryColor }) => {
  return (
    <CardContainer categoryColor={categoryColor}>
      {course.featured && <FeaturedBadge>Featured</FeaturedBadge>}
      
      <CourseHeader>
        <CourseCode categoryColor={categoryColor}>
          {course.code}
        </CourseCode>
        <LevelBadge categoryColor={categoryColor}>
          {course.level}
        </LevelBadge>
      </CourseHeader>

      <CourseTitle categoryColor={categoryColor}>
        {course.title}
      </CourseTitle>

      <CourseDescription>
        {course.about}
      </CourseDescription>

      <CourseDetails>
        <DetailItem categoryColor={categoryColor}>
          <FiAward />
          <span>{course.level}</span>
        </DetailItem>
        <DetailItem categoryColor={categoryColor}>
          <FiUsers />
          <span>Nationally Recognized</span>
        </DetailItem>
      </CourseDetails>

      <JobOutcomes>
        <JobOutcomesTitle>
          <FiUsers />
          Career Outcomes
        </JobOutcomesTitle>
        <JobOutcomesList>
          {course.jobOutcomes}
        </JobOutcomesList>
      </JobOutcomes>

      <EntryRequirements>
        <EntryRequirementsTitle>
          Entry Requirements
        </EntryRequirementsTitle>
        <EntryRequirementsText>
          {course.entryRequirements}
        </EntryRequirementsText>
      </EntryRequirements>

      <ViewButton to={`/courses/${course.code}`} categoryColor={categoryColor}>
        View Details
        <FiArrowRight />
      </ViewButton>
    </CardContainer>
  );
};

export default CourseCard; 